/**
 * Static id → title catalog for in-game items. The PlayFab "primary" catalog
 * is for IAP, so we maintain this list manually via src/data/items.json.
 * Order is preserved for the picker's default view; users can also search
 * by name or id. To re-populate, overwrite items.json with a fresh export
 * from the game — no other files need to change.
 *
 * Two transforms happen at import time so the JSON can stay a faithful
 * mirror of the game export:
 *   1. Dedupe by id. The export emits some entries twice (e.g. mat_ore_2,
 *      halloween_profile_claris). We keep the first and warn on the rest.
 *   2. Reclassify cosmetics. The export buckets every appearance/skin under
 *      "equipment" alongside real combat gear. We split them out: anything
 *      tagged "equipment" whose id does NOT start with `eq_` becomes
 *      "costume" instead. Real gear (eq_01_sword, eq_gale_bow, …) keeps
 *      "equipment".
 */

import itemsJson from "@/data/items.json";

export type ItemCategory =
  | "consumable"
  | "material"
  | "rune"
  | "keyitem"
  | "equipment"
  | "costume";

export interface ItemCatalogEntry {
  id: string;
  name: string;
  category?: ItemCategory;
}

interface RawItem {
  id: string;
  name: string;
  category: string;
}

function reclassify(raw: RawItem): ItemCategory | undefined {
  const c = raw.category;
  if (c === "equipment" && !raw.id.startsWith("eq_")) return "costume";
  if (
    c === "consumable" ||
    c === "material" ||
    c === "rune" ||
    c === "keyitem" ||
    c === "equipment"
  ) {
    return c;
  }
  return undefined;
}

const seen = new Set<string>();
const allItems: ItemCatalogEntry[] = [];
const droppedDupes: string[] = [];
for (const raw of itemsJson as RawItem[]) {
  if (seen.has(raw.id)) {
    droppedDupes.push(raw.id);
    continue;
  }
  seen.add(raw.id);
  allItems.push({ id: raw.id, name: raw.name, category: reclassify(raw) });
}
if (droppedDupes.length > 0 && typeof console !== "undefined") {
  // eslint-disable-next-line no-console
  console.warn(
    `[item-catalog] dropped ${droppedDupes.length} duplicate id(s) from items.json:`,
    droppedDupes
  );
}

/**
 * IDs the picker should never offer. Mods can't grant a player their own
 * "default" appearance — these only exist so the cloud-data viewer can label
 * them when they show up in save blobs.
 */
function isPickable(it: ItemCatalogEntry): boolean {
  if (it.id.startsWith("default_")) return false;
  return true;
}

export const ITEM_CATALOG: ItemCatalogEntry[] = allItems.filter(isPickable);

// Lookup map covers ALL items (including non-pickable ones) so itemTitle()
// still resolves names for ids that show up in player inventories.
export const ITEM_NAME_BY_ID: Map<string, string> = new Map(
  allItems.map((it) => [it.id, it.name])
);

export function itemTitle(id: string): string {
  return ITEM_NAME_BY_ID.get(id) ?? id;
}
